import { Injectable, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';

export type Theme = 'legacy' | 'primeng';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private doc = inject(DOCUMENT);
  private router = inject(Router);
  private current: Theme | null = null;
  private currentModuleClass: string | null = null;
  private pendingTheme: Theme | null = null;
  private pendingModuleClass: string | null = null;

  private currentThemeSubject = new BehaviorSubject<Theme | null>(null);
  currentTheme$ = this.currentThemeSubject.asObservable();

  constructor() {
    // Apply queued theme transitions only after the in-flight navigation has
    // committed. Doing the swap synchronously inside a guard re-renders the
    // layout's @if branches mid-navigation, which destroys/recreates the
    // <router-outlet> and causes Angular to cancel the navigation.
    this.router.events.subscribe(e => {
      if (e instanceof NavigationStart) {
        this.pendingTheme = null;
        this.pendingModuleClass = null;
      } else if (e instanceof NavigationEnd) {
        if (
          this.pendingTheme &&
          (this.pendingTheme !== this.current || this.pendingModuleClass !== this.currentModuleClass)
        ) {
          this.applyImmediate(this.pendingTheme, this.pendingModuleClass);
        }
        this.pendingTheme = null;
        this.pendingModuleClass = null;
      }
    });
  }

  apply(theme: Theme, moduleClass?: string): void {
    const nextModuleClass = moduleClass ?? null;
    if (this.current === theme && this.currentModuleClass === nextModuleClass) return;
    if (this.current === null) {
      // First call (initial page bootstrap): apply synchronously so the
      // layout has a theme to render against on its first paint.
      this.applyImmediate(theme, nextModuleClass);
      return;
    }
    // Subsequent calls happen inside route guards; defer until NavigationEnd.
    this.pendingTheme = theme;
    this.pendingModuleClass = nextModuleClass;
  }

  private applyImmediate(theme: Theme, moduleClass: string | null): void {
    if (this.current === theme && this.currentModuleClass === moduleClass) return;
    this.current = theme;
    this.currentThemeSubject.next(theme);

    if (theme === 'legacy') {
      this.setThemeClass('legacy-theme');
      this.removeStyleClass('prime-ng-theme');
    } else {
      this.setThemeClass('prime-ng-theme');
      this.removeStyleClass('legacy-theme');
    }

    if (this.currentModuleClass && this.currentModuleClass !== moduleClass) {
      this.removeStyleClass(this.currentModuleClass);
    }
    if (moduleClass) {
      this.setThemeClass(moduleClass);
    }
    this.currentModuleClass = moduleClass;
  }

  private setThemeClass(className: string): void {
    this.doc.documentElement.classList.add(className);
  }

  private removeStyleClass(className: string): void {
    this.doc.documentElement.classList.remove(className);
  }
}
